import * as React from 'react';
import { Switch, Route, Redirect } from 'react-router';
import URLS from '../../urls';
import { isContributable, useLocale } from '../locale-helpers';
import DocumentPage from '../pages/document-page';
import { Spinner } from '../ui/ui';
import API from '../../services/api';
import store from '../../stores/root';
const LoginPage = React.lazy(() => import('../pages/login/login-page'));
const HomePage = React.lazy(() => import('../pages/home/home'));
const UserPage = React.lazy(() => import('../pages/user-page/user-page'));
const AdminPage = React.lazy(() => import('../pages/admin-page/admin-page'));
const CampaingDetail = React.lazy(
  () => import('../pages/campaing-detail/campaing-detail')
);
const DevicesPage = React.lazy(() => import('../pages/devices/devices-page'));
const DatasetsPage = React.lazy(() => import('../pages/datasets/datasets'));
const LanguagesPages = React.lazy(() => import('../pages/languages/languages'));
const DashboardPage = React.lazy(() => import('../pages/dashboard/dashboard'));
const ProfileLayoutPage = React.lazy(() => import('../pages/profile/layout'));
const FAQPage = React.lazy(() => import('../pages/faq/faq'));
const AboutPage = React.lazy(() => import('../pages/about/about'));
const LandingPage = React.lazy(() => import('../pages/landing/landing'));
const ErrorPage = React.lazy(() => import('../pages/error-page/error-page'));
const CriteriaPage = React.lazy(() => import('../pages/criteria/criteria'));
const KeywordsPage = React.lazy(
  () => import('../pages/keywords-page/keywords-page')
);
const CampaingsPage = React.lazy(
  () => import('../pages/campaings-page/campaings-page')
);

type PrivateRouteType = {
  exact?: boolean;
  path: any;
  admin?: boolean;
  component: React.ComponentType<any>;
};

const PrivateRoute: React.ComponentType<PrivateRouteType> = props => {
  if (
    !store.getState().login ||
    (props.admin && store.getState().login != 'ADMIN')
  ) {
    return <Redirect to={URLS.LOGIN + '?redirect=' + props.path} />;
  }

  return (
    <Route
      exact={props.exact}
      path={props.path}
      component={props.component}
      {...props}
    />
  );
};

export default function Content({
  location,
  api,
}: {
  location: any;
  api: API;
}) {
  const [locale, toLocaleRoute] = useLocale();
  return (
    <div id="content">
      <React.Suspense fallback={<Spinner />}>
        <Switch>
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.ROOT)}
            component={HomePage}
          />

          <Route
            exact
            path={toLocaleRoute(URLS.LOGIN)}
            render={() => <LoginPage api={api} />}
          />

          <PrivateRoute
            exact
            path={toLocaleRoute('/new')}
            component={() => <Redirect to={toLocaleRoute(URLS.ROOT)} />}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.RECORD)}
            component={() => (
              <Redirect
                to={toLocaleRoute(
                  isContributable(locale) ? URLS.SPEAK : URLS.ROOT
                )}
              />
            )}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.LANGUAGES)}
            component={LanguagesPages}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.DATA)}
            component={() => <Redirect to={toLocaleRoute(URLS.DATASETS)} />}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.DATASETS)}
            component={DatasetsPage}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.USER)}
            component={UserPage}
          />
          {/* ADMIN */}
          <PrivateRoute
            exact
            admin
            path={toLocaleRoute(URLS.ADMIN)}
            component={() => <AdminPage api={api} />}
          />
          <PrivateRoute
            exact
            admin
            path={toLocaleRoute(URLS.CAMPAING_NEW)}
            component={() => <CampaingDetail api={api} />}
          />
          <PrivateRoute
            exact
            admin
            path={toLocaleRoute(URLS.CAMPAING_EDIT + ':id')}
            component={() => <CampaingDetail api={api} />}
          />
          <PrivateRoute
            exact
            admin
            path={toLocaleRoute(URLS.KEYWORDS)}
            component={() => <KeywordsPage api={api} />}
          />
          <PrivateRoute
            exact
            admin
            path={toLocaleRoute(URLS.CAMPAINGS)}
            component={() => <CampaingsPage api={api} />}
          />
          <PrivateRoute
            exact
            admin
            path={toLocaleRoute(URLS.DEVICES)}
            component={() => <DevicesPage api={api} />}
          />
          {/* END ADMIN */}

          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.PROFILE)}
            component={() => <Redirect to={toLocaleRoute(URLS.PROFILE_INFO)} />}
          />
          <PrivateRoute
            path={toLocaleRoute(URLS.PROFILE + '/')}
            component={ProfileLayoutPage}
          />
          <PrivateRoute
            path={toLocaleRoute(URLS.DASHBOARD)}
            component={DashboardPage}
          />
          <PrivateRoute
            path={toLocaleRoute(URLS.CRITERIA)}
            component={CriteriaPage}
          />
          {[URLS.CHALLENGE, URLS.STATS, URLS.GOALS, URLS.AWARDS].map(path => (
            <PrivateRoute
              key={path}
              exact
              path={toLocaleRoute(path)}
              component={() => (
                <Redirect to={toLocaleRoute(URLS.DASHBOARD + path)} />
              )}
            />
          ))}
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.PROFILE_GOALS)}
            component={() => (
              <Redirect to={toLocaleRoute(URLS.DASHBOARD + '/' + URLS.GOALS)} />
            )}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.FAQ)}
            component={FAQPage}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.ABOUT)}
            component={AboutPage}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.PRIVACY)}
            component={() => <DocumentPage key="p" name="privacy" />}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.TERMS)}
            component={() => <DocumentPage key="t" name="terms" />}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.CHALLENGE_TERMS)}
            component={() => <DocumentPage key="c" name="challenge-terms" />}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute('/landing/sodedif')}
            component={LandingPage}
          />
          <Route
            path={toLocaleRoute('/404')}
            component={() => (
              <ErrorPage errorCode="404" prevPath={location.state?.prevPath} />
            )}
          />
          <Route
            path={toLocaleRoute('/503')}
            component={() => (
              <ErrorPage errorCode="503" prevPath={location.state?.prevPath} />
            )}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.SPEAK)}
            component={() => {
              // note: this is redundant with routing in LocalizedPage in app.tsx, and hanldes
              // locale changing edge cases where toLocaleRoute is still using the old locale
              return <Redirect to={toLocaleRoute(URLS.SPEAK)} />;
            }}
          />
          <PrivateRoute
            exact
            path={toLocaleRoute(URLS.LISTEN)}
            component={() => {
              // note: this is redundant with routing in LocalizedPage in app.tsx, and hanldes
              // locale changing edge cases where toLocaleRoute is still using the old locale
              return <Redirect to={toLocaleRoute(URLS.LISTEN)} />;
            }}
          />
          <Redirect
            push
            to={{
              pathname: toLocaleRoute('/404'),
              state: { prevPath: location.pathname },
            }}
          />
        </Switch>
      </React.Suspense>
    </div>
  );
}
